import { GameConfigurator, ConfigSchema } from '../../interfaces/game-type-plugin.interface';
import { WheelGameSettings } from '@kanek/shared-data-access';
import { WheelConfigMapper } from './wheel-config-mapper';

/**
 * Wheel game configurator.
 * Provides default settings, validation, and UI schema for wheel game configuration.
 */
export class WheelConfigurator implements GameConfigurator {
  getDefaultConfig(): WheelGameSettings {
    return WheelConfigMapper.getDefaultSettings();
  }

  validateConfig(config: unknown): string[] {
    const errors: string[] = [];
    const settings = config as WheelGameSettings;

    // Type check
    if (settings.type !== 'wheel') {
      errors.push('Invalid game type: expected "wheel"');
    }

    // Radius validation
    if (settings.radius < 0 || settings.radius > 1) {
      errors.push('Radius must be between 0 and 1');
    }

    // Border width validation
    if (settings.borderWidth < 0) {
      errors.push('Border width must be non-negative');
    }

    // Label radius validation
    if (settings.itemLabelRadius < 0 || settings.itemLabelRadius > 1) {
      errors.push('Label radius must be between 0 and 1');
    }

    // Label alignment validation (only left/right allowed)
    if (settings.itemLabelAlign && !['left', 'right'].includes(settings.itemLabelAlign)) {
      errors.push('Label alignment must be "left" or "right"');
    }

    // Font size validation
    if (settings.itemLabelFontSizeMax < 1) {
      errors.push('Font size must be at least 1');
    }

    // Rotation speed validation
    if (settings.rotationSpeedMax < 0) {
      errors.push('Rotation speed must be non-negative');
    }

    // Color array validations
    if (!settings.itemLabelColors || settings.itemLabelColors.length === 0) {
      errors.push('At least one label color is required');
    }

    if (!settings.itemBackgroundColors || settings.itemBackgroundColors.length === 0) {
      errors.push('At least one background color is required');
    }

    return errors;
  }

  getConfigSchema(): ConfigSchema {
    return {
      sections: [
        {
          title: 'Appearance',
          description: 'Visual settings for the wheel',
          fields: [
            {
              key: 'radius',
              label: 'Wheel Size',
              type: 'range',
              defaultValue: 0.84,
              description: 'Size of the wheel relative to container (0-1)',
              validation: {
                required: true,
                min: 0.1,
                max: 1,
                message: 'Radius must be between 0.1 and 1',
              },
            },
            {
              key: 'borderColor',
              label: 'Border Color',
              type: 'color',
              defaultValue: '#fff',
              description: 'Color of the wheel border',
            },
            {
              key: 'borderWidth',
              label: 'Border Width',
              type: 'number',
              defaultValue: 1,
              description: 'Width of the wheel border in pixels',
              validation: {
                required: true,
                min: 0,
                message: 'Border width must be non-negative',
              },
            },
            {
              key: 'lineColor',
              label: 'Line Color',
              type: 'color',
              defaultValue: '#fff',
              description: 'Color of segment divider lines',
            },
            {
              key: 'lineWidth',
              label: 'Line Width',
              type: 'number',
              defaultValue: 1,
              description: 'Width of segment divider lines in pixels',
            },
          ],
        },
        {
          title: 'Labels',
          description: 'Text label settings for wheel segments',
          fields: [
            {
              key: 'itemLabelFontSizeMax',
              label: 'Font Size',
              type: 'number',
              defaultValue: 55,
              description: 'Maximum font size for labels',
              validation: {
                required: true,
                min: 1,
                message: 'Font size must be at least 1',
              },
            },
            {
              key: 'itemLabelRadius',
              label: 'Label Position',
              type: 'range',
              defaultValue: 0.93,
              description: 'Distance from center (0-1)',
              validation: {
                required: true,
                min: 0,
                max: 1,
                message: 'Label radius must be between 0 and 1',
              },
            },
            {
              key: 'itemLabelAlign',
              label: 'Label Alignment',
              type: 'select',
              defaultValue: 'left',
              description: 'Text alignment for labels',
              options: [
                { label: 'Left', value: 'left' },
                { label: 'Right', value: 'right' },
              ],
            },
            {
              key: 'itemLabelRotation',
              label: 'Label Rotation',
              type: 'number',
              defaultValue: 180,
              description: 'Rotation angle for labels in degrees',
            },
            {
              key: 'itemLabelStrokeWidth',
              label: 'Label Stroke Width',
              type: 'number',
              defaultValue: 0,
              description: 'Width of label text outline',
            },
            {
              key: 'itemLabelStrokeColor',
              label: 'Label Stroke Color',
              type: 'color',
              defaultValue: '#000',
              description: 'Color of label text outline',
            },
          ],
        },
        {
          title: 'Behavior',
          description: 'Interaction and animation settings',
          fields: [
            {
              key: 'isInteractive',
              label: 'Interactive',
              type: 'boolean',
              defaultValue: false,
              description: 'Allow manual spinning via drag/swipe',
            },
            {
              key: 'rotationSpeedMax',
              label: 'Spin Speed',
              type: 'number',
              defaultValue: 500,
              description: 'Maximum rotation speed',
              validation: {
                required: true,
                min: 0,
                message: 'Speed must be non-negative',
              },
            },
            {
              key: 'rotationResistance',
              label: 'Rotation Resistance',
              type: 'number',
              defaultValue: 0,
              description: 'Resistance/friction for spin deceleration',
              validation: {
                min: 0,
                message: 'Resistance must be non-negative',
              },
            },
          ],
        },
      ],
    };
  }
}
